import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "@/assets/HR.png";
import DensityToggle from "@/components/DensityToggle";
import Icon from "@/components/Icons";
import ShellBanners from "@/components/ShellBanners";
import ThemePicker from "@/components/ThemePicker";
import useShell from "@/lib/useShell";
import { groupKey, groupIcon } from "@/lib/nav";

/** Aspectul Purple: bară sus, sertar cu meniul, pagină de start cu plăci, cale de pâine, meniu ⋮ pe rânduri (fostul aspect portocaliu, în mov și roz). */
export default function LayoutPurple({ children }) {
  const { user, nav, unread, platformVersion, pwa, iosHelp, setIosHelp, newBuild, licenseAlert, updateBanner, doLogout, roleLabel, groups } = useShell();
  const loc = useLocation();
  const [navOpen, setNavOpen] = useState(false); // sertarul de meniu
  const [userOpen, setUserOpen] = useState(false);
  useEffect(() => { setNavOpen(false); setUserOpen(false); }, [loc.pathname]);
  // Calea de pâine: Acasă › grup › pagină
  const crumbs = (() => {
    if (loc.pathname === "/") return [];
    const out = [];
    for (const g of groups) {
      const it = g.items.find(i => i.to === loc.pathname) || g.items.filter(i => i.to !== "/" && loc.pathname.startsWith(i.to + "/")).sort((a, b) => b.to.length - a.to.length)[0];
      if (it) { if (g.title !== "Dashboard") out.push({ label: g.title.replace("PlanCo - ", ""), to: `/m/${groupKey(g)}` }); out.push({ label: it.label }); break; }
      if (loc.pathname === `/m/${groupKey(g)}`) { out.push({ label: g.title }); break; }
    }
    return out;
  })();
  // Un grup cu o singură pagină duce direct la ea; altfel la pagina de modul cu plăci
  const groupTarget = (g) => (g.items.length === 1 ? g.items[0].to : g.title === "Dashboard" ? "/" : `/m/${groupKey(g)}`);

  return (
    <div className="app-shell">
      <div className={"drawer-overlay" + (navOpen ? " show" : "")} onClick={() => setNavOpen(false)} data-testid="nav-overlay" />
      <aside className={"drawer" + (navOpen ? " open" : "")} data-testid="app-sidebar" aria-hidden={!navOpen}>
        <div className="drawer-head">
          <Link to="/" onClick={() => setNavOpen(false)} data-testid="sidebar-logo-link" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#1F1F1F" }}>
            <img src={logo} alt="ONOMAD" />
          </Link>
          <button type="button" className="topbar-btn" onClick={() => setNavOpen(false)} aria-label="Închide meniul"><Icon name="close" size={22} /></button>
        </div>
        <nav className="drawer-nav">
          <NavLink to="/" end className={({ isActive }) => "drawer-link" + (isActive ? " active" : "")} data-testid="nav-home"><span className="ico" style={{ color: "var(--ac-500)", display: "inline-flex" }}><Icon name="home" /></span><span>Acasă</span></NavLink>
          {groups.filter(g => g.title !== "Dashboard").map(g => (
            <NavLink key={g.title} to={groupTarget(g)} data-testid={`nav-group-${groupKey(g)}`}
              className={({ isActive }) => "drawer-link" + (isActive || g.items.some(i => i.to !== "/" && loc.pathname.startsWith(i.to)) ? " active" : "")}>
              <span className="ico" style={{ color: "var(--ac-500)", display: "inline-flex" }}><Icon name={groupIcon(g)} /></span><span>{g.title.replace("PlanCo - ", "")}</span>
            </NavLink>))}
          {groups.some(g => g.items.some(i => i.to === "/notifications")) && (
            <NavLink to="/notifications" className={({ isActive }) => "drawer-link" + (isActive ? " active" : "")} data-testid="nav-notifications">
              <span className="ico" style={{ color: "var(--ac-500)", display: "inline-flex" }}><Icon name="bell" /></span><span>Notificări</span>
              {unread > 0 && <span className="drawer-badge" data-testid="notif-badge">{unread}</span>}
            </NavLink>)}
        </nav>
        <div className="drawer-foot">{platformVersion ? <span data-testid="platform-version">Versiunea {platformVersion}</span> : null}</div>
      </aside>
      <main className="app-main">
        <header className="topbar">
          <button type="button" className="topbar-btn" aria-label="Deschide meniul" aria-expanded={navOpen} onClick={() => setNavOpen(o => !o)} data-testid="menu-toggle"><Icon name="menu" size={24} /></button>
          <Link to="/" className="topbar-brand"><img src={logo} alt="" /><span className="name">Platforma de HR</span></Link>
          <span className="topbar-spacer" />
          <DensityToggle inline />
          <button type="button" className="topbar-btn round" aria-label="Contul meu" aria-expanded={userOpen} onClick={() => setUserOpen(o => !o)} data-testid="user-menu"><Icon name="user" size={24} />{unread > 0 && <span style={{ position: "absolute", marginTop: -26, marginLeft: 28, width: 10, height: 10, background: "var(--ac-500)", borderRadius: "50%" }} />}</button>
        </header>
        {userOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 70 }} onClick={() => setUserOpen(false)} />
            <div className="user-pop" data-testid="user-pop">
              <div className="who" data-testid="current-user">{user?.name || user?.username}</div>
              <div className="roles">{roleLabel}</div>
              <button className="btn btn-secondary" onClick={() => nav("/my-account")} data-testid="menu-my-account"><Icon name="user" size={18} />Contul meu</button>
              <button className="btn btn-secondary" onClick={() => nav("/notifications")} data-testid="menu-notifications"><Icon name="bell" size={18} />Notificări{unread > 0 ? ` (${unread})` : ""}</button>
              {(pwa.canInstall || pwa.ios) && <button className="btn btn-secondary" data-testid="install-app" onClick={() => (pwa.canInstall ? pwa.install() : setIosHelp(v => !v))}><Icon name="phone" size={18} />Instalează aplicația</button>}
              {iosHelp && <div style={{ fontSize: 12, color: "#555", background: "#EEE", borderRadius: 8, padding: "8px 10px", marginBottom: 8, lineHeight: 1.5 }} data-testid="ios-help">Pe iPhone/iPad: apasă <b>Distribuie</b> din Safari, apoi <b>Adaugă pe ecranul principal</b>.</div>}
              <ThemePicker />
              <button className="btn btn-primary" onClick={doLogout} data-testid="logout-btn" style={{ marginBottom: 0 }}><Icon name="logout" size={18} />Deconectare</button>
            </div>
          </>)}
        <ShellBanners licenseAlert={licenseAlert} newBuild={newBuild} updateBanner={updateBanner} />
        {crumbs.length > 0 && (
          <nav className="crumbs" aria-label="Calea paginii" data-testid="crumbs">
            <Link to="/"><Icon name="home" size={22} />Acasă</Link>
            {crumbs.map((c, i) => (<React.Fragment key={i}><Icon name="chevron" size={14} />{c.to ? <Link to={c.to}>{c.label}</Link> : <span className="cur">{c.label}</span>}</React.Fragment>))}
          </nav>)}
        <div className="app-content">{children}</div>
      </main>
    </div>
  );
}

